import { PopupCookie } from './popup-cookie.js';

export const selectors$b = {
  open: '[data-popup-open]',
  close: '[data-popup-close]',
  dialog: 'dialog',
  focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
  newsletterForm: '[data-newsletter-form]',
  newsletterHeading: '[data-newsletter-heading]',
  newsletterField: '[data-newsletter-field]',
};

const attributes$8 = {
  closing: 'closing',
  delay: 'data-popup-delay',
  scrollLock: 'data-scroll-lock-required',
  cookieName: 'data-cookie-name',
  cookieValue: 'data-cookie-value',
  preventTopLayer: 'data-prevent-top-layer',
};

export const classes$8 = {
  hidden: 'hidden',
  hasValue: 'has-value',
  cartBarVisible: 'cart-bar-visible',
  isVisible: 'is-visible',
  success: 'has-success',
  mobile: 'mobile',
  desktop: 'desktop',
  bottom: 'bottom',
};

export class PopupComponent extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector(selectors$b.dialog);
    this.preventTopLayer = this.popup.hasAttribute(attributes$8.preventTopLayer);
    this.enableScrollLock = this.popup.hasAttribute(attributes$8.scrollLock);
    this.buttonPopupOpen = this.querySelector(selectors$b.open);
    this.a11y = window.theme.a11y;
    this.isAnimating = false;
    this.cookie = new PopupCookie(this.popup.getAttribute(attributes$8.cookieName), this.popup.getAttribute(attributes$8.cookieValue));

    this.checkTargetReferrer();
    this.checkCookie();
    this.bindListeners();
  }

  checkTargetReferrer() {
    if (!this.popup.hasAttribute(attributes$8.referrer)) return;

    if (location.href.indexOf(this.popup.getAttribute(attributes$8.referrer)) === -1 && !window.Shopify.designMode) {
      this.popup.parentNode.removeChild(this.popup);
    }
  }

  checkCookie() {
    const cookieExists = this.cookie && this.cookie.read() !== false;

    if (!cookieExists) {
      this.showPopupEvents();

      this.popup.addEventListener('theme:popup:onclose', () => this.cookie.write());
    }
  }

  bindListeners() {
    // Open button click event
    this.buttonPopupOpen?.addEventListener('click', (e) => {
      e.preventDefault();
      this.popupOpen();
      window.theme.a11y.lastElement = this.buttonPopupOpen;
    });

    // Close button click event
    this.popup.querySelectorAll(selectors$b.close)?.forEach((closeButton) => {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.popupClose();
      });
    });

    // Close dialog on click outside content
    this.popup.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
        this.popupClose();
      }
    });

    // Close dialog on click ESC key pressed
    this.popup.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        this.popupClose();
      }
    });

    this.popup.addEventListener('close', () => this.popupCloseActions());
  }

  popupOpen() {
    this.isAnimating = true;

    // Check if browser supports Dialog tags
    if (typeof this.popup.showModal === 'function' && !this.preventTopLayer) {
      this.popup.showModal();
    } else if (typeof this.popup.show === 'function') {
      this.popup.show();
    } else {
      this.popup.setAttribute('open', '');
    }

    this.popup.removeAttribute('inert');
    this.popup.setAttribute('aria-hidden', false);
    this.popup.focus(); // Focus <dialog> tag element to prevent immediate closing on Escape keypress

    if (this.enableScrollLock) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    }

    window.theme.waitForAnimationEnd(this.popup).then(() => {
      this.isAnimating = false;

      if (this.enableScrollLock) {
        this.a11y.trapFocus(this.popup);
      }

      const focusTarget = this.popup.querySelector('[autofocus]') || this.popup.querySelector(selectors$b.focusable);
      focusTarget?.focus();
    });
  }

  popupClose() {
    if (this.isAnimating || this.popup.hasAttribute('inert')) {
      return;
    }

    if (!this.popup.hasAttribute(attributes$8.closing)) {
      this.popup.setAttribute(attributes$8.closing, '');
      this.isAnimating = true;

      window.theme.waitForAnimationEnd(this.popup).then(() => {
        this.isAnimating = false;
        this.popupClose();
      });

      return;
    }

    // Check if browser supports Dialog tags
    if (typeof this.popup.close === 'function') {
      this.popup.close();
    } else {
      this.popup.removeAttribute('open');
      this.popup.setAttribute('aria-hidden', true);
    }

    this.popupCloseActions();
  }

  popupCloseActions() {
    if (this.popup.hasAttribute('inert')) return;

    this.popup.setAttribute('inert', '');
    this.popup.setAttribute('aria-hidden', true);
    this.popup.removeAttribute(attributes$8.closing);

    // Unlock scroll if no other popups & modals are open
    if (!window.theme.hasOpenModals() && this.enableScrollLock) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }

    this.popup.dispatchEvent(new CustomEvent('theme:popup:onclose', { bubbles: false }));

    if (this.enableScrollLock) {
      this.a11y.removeTrapFocus();
      this.a11y.autoFocusLastElement();
    }
  }

  showPopupEvents() {
    // Auto show popup if it has open attribute
    if (this.popup.hasAttribute('open') && this.popup.getAttribute('open') == true) {
      this.popupOpen();
    }

    this.delay = this.popup.hasAttribute(attributes$8.delay) ? this.popup.getAttribute(attributes$8.delay) : null;
    this.isSubmitted = window.location.href.indexOf('accepts_marketing') !== -1 || window.location.href.indexOf('customer_posted=true') !== -1;

    if (this.showOnScrollEvent) {
      document.removeEventListener('theme:scroll', this.showOnScrollEvent);
    }

    this.showOnScrollEvent = window.theme.throttle(this.showOnScroll.bind(this), 200);

    if (this.delay === 'always' || this.isSubmitted) {
      this.popupOpen();
    }

    if (this.delay && this.delay.includes('delayed') && !this.isSubmitted) {
      this.showDelayed();
    }

    if (this.delay === 'bottom' && !this.isSubmitted) {
      this.showOnBottomReached();
    }

    if (this.delay === 'idle' && !this.isSubmitted) {
      this.showOnIdle();
    }
  }

  showDelayed() {
    const seconds = this.delay.includes('_') ? parseInt(this.delay.split('_')[1]) : 10;

    // Show popup after specific seconds
    setTimeout(() => {
      this.popupOpen();
    }, seconds * 1000);
  }

  showOnIdle() {
    let timer = 0;
    let idleTime = 60000;
    const documentEvents = ['mousemove', 'mousedown', 'click', 'touchmove', 'touchstart', 'touchend', 'keydown', 'keypress'];
    const windowEvents = ['load', 'resize', 'scroll'];

    const startTimer = () => {
      timer = setTimeout(() => {
        timer = 0;
        this.popupOpen();
      }, idleTime);

      documentEvents.forEach((eventType) => {
        document.addEventListener(eventType, resetTimer);
      });

      windowEvents.forEach((eventType) => {
        window.addEventListener(eventType, resetTimer);
      });
    };

    const resetTimer = () => {
      if (timer) {
        clearTimeout(timer);
      }

      documentEvents.forEach((eventType) => {
        document.removeEventListener(eventType, resetTimer);
      });

      windowEvents.forEach((eventType) => {
        window.removeEventListener(eventType, resetTimer);
      });

      startTimer();
    };

    startTimer();
  }

  showOnBottomReached() {
    document.addEventListener('theme:scroll', this.showOnScrollEvent);
  }

  showOnScroll() {
    if (window.scrollY + window.innerHeight >= document.body.clientHeight) {
      this.popupOpen();
      document.removeEventListener('theme:scroll', this.showOnScrollEvent);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('theme:scroll', this.showOnScrollEvent);
  }
}

if (!customElements.get('popup-component')) {
  customElements.define('popup-component', PopupComponent);
}
